import { useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePersons } from "@/hooks/usePersons";
import { findRelation } from "@/lib/relation";
import { Avatar } from "@/components/PersonCard";
import { t as tr } from "@/lib/i18n";

const sel = "w-full h-12 rounded-xl border border-[#E5DEC9] bg-white px-3 text-sm";

const Picker = ({ label, value, onChange, persons, testid }) => (
  <div className="space-y-1.5 flex-1">
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7E8E85]">{label}</p>
    <select value={value} onChange={(e) => onChange(e.target.value)} className={sel} data-testid={testid}>
      <option value="">{tr("Choose a family member")}</option>
      {persons.map((p) => <option key={p.person_id} value={p.person_id}>{p.name}{p.birth_year ? ` (${p.birth_year})` : ""}</option>)}
    </select>
  </div>
);

const Result = ({ a, b, r, t, isMe }) => (
  <div className="rounded-3xl border border-[#E5DEC9] bg-white p-8 space-y-6 rise" data-testid="relation-result">
    {r === null ? (
      <p className="font-serif text-2xl" data-testid="relation-none">No connection found yet between {a.name} and {b.name}.</p>
    ) : r.same ? (
      <p className="font-serif text-2xl" data-testid="relation-same">{tr("That's the same person.")}</p>
    ) : (
      <>
        <div>
          <p className="text-[#52635B]"><b>{b.name}</b> is {isMe ? "your" : <><b>{a.name}</b>'s</>}</p>
          <p className="font-serif text-3xl sm:text-4xl font-bold text-[#1E3A2B] mt-1" data-testid="relation-chain">{r.chain}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {r.hindi && <span className="px-4 py-1.5 rounded-full bg-[#D96B43] text-white font-medium" data-testid="relation-hindi">{r.hindi}</span>}
            {r.english && <span className="px-4 py-1.5 rounded-full bg-[#F4EDE2] text-[#1E3A2B] capitalize" data-testid="relation-english">{r.english}</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="relation-path">
          {r.path.map((id, i) => (
            <span key={id} className="flex items-center gap-2">
              {i > 0 && <ArrowRight className="w-4 h-4 text-[#CDBF9F]" />}
              <Link to={`/person/${id}`} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-[#E5DEC9] hover:border-[#1E3A2B] transition-colors">
                <Avatar p={t.byId[id]} size="w-7 h-7 text-[10px]" /><span className="text-sm">{t.byId[id].name}</span>
              </Link>
            </span>
          ))}
        </div>
      </>
    )}
  </div>
);

export default function Relations() {
  const { user } = useAuth();
  const { persons, t } = usePersons();
  const [params, setParams] = useSearchParams();
  const myId = user?.person?.status === "approved" ? user.person.person_id : "";
  const aId = params.get("a") || myId;
  const bId = params.get("b") || "";
  const set = (k) => (v) => { const next = new URLSearchParams(params); next.set("a", aId); next.set(k, v); setParams(next, { replace: true }); };
  const sorted = useMemo(() => [...(persons || [])].sort((x, y) => x.name.localeCompare(y.name)), [persons]);
  const r = useMemo(() => (t.byId[aId] && t.byId[bId] ? findRelation(aId, bId, t) : undefined), [aId, bId, t]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-14 space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-3 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" />Relationship finder</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight">{tr("How are we related?")}</h1>
        <p className="mt-3 text-[#52635B]">{tr("Pick any two people to see the exact relation — in English and Hindi.")}</p>
      </div>
      {persons === null ? <div className="h-32 rounded-3xl bg-[#F4EDE2] animate-pulse" /> : (
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <Picker label={tr("From")} value={aId} onChange={set("a")} persons={sorted} testid="relation-a-select" />
          <ArrowRight className="hidden sm:block w-5 h-5 mb-3.5 text-[#CDBF9F]" />
          <Picker label={tr("To")} value={bId} onChange={set("b")} persons={sorted} testid="relation-b-select" />
        </div>
      )}
      {r !== undefined && <Result a={t.byId[aId]} b={t.byId[bId]} r={r} t={t} isMe={aId === myId} />}
    </div>
  );
}
